import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { IconType } from 'react-icons';
import {
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiCheckSquare,
  FiClipboard,
  FiDollarSign,
  FiGrid,
  FiList,
  FiLogOut,
  FiPieChart,
  FiSettings,
  FiShield,
  FiTarget,
  FiUsers,
  FiMessageCircle,
} from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { useWhatsAppEnabled } from '@hooks/useWhatsAppEnabled';
import { useImplantacaoConsole } from '@hooks/useImplantacaoConsole';
import { usePodeVerClientesHub } from '@hooks/usePodeVerClientesHub';
import { useListaVidasPapel } from '@hooks/useListaVidasPapel';
import { prefetchRoute } from '@queries';
import { isAexWorkspace, isEnterpriseWorkspace } from '@config/enterprise';
import logoHorizontal from '@assets/logo ED Horizontal.png';
import SyncStatus from '@components/Navigation/SyncStatus';
import NotificationBell from '@components/Notifications/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { WorkspaceMembership } from '@/types/auth';

interface NavItem {
  id: string;
  label: string;
  view: string;
  icon: IconType;
}

interface NavigationProps {
  activeView: string;
  onViewChange: (view: string) => void;
  currentPath?: string;
}

const Navigation = ({ activeView, onViewChange }: NavigationProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout, isSuperAdmin, profile, workspace, listWorkspaces, switchWorkspace, userRole } = useAuth();
  const [workspaceOptions, setWorkspaceOptions] = useState<WorkspaceMembership[]>([]);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceSwitching, setWorkspaceSwitching] = useState<string | null>(null);
  const enterpriseWorkspace = isEnterpriseWorkspace(workspace);
  const { enabled: whatsappEnabled } = useWhatsAppEnabled();
  const { enabled: implantacaoEnabled } = useImplantacaoConsole();
  const { enabled: hubEnabled } = usePodeVerClientesHub();
  const clientesEnabled = implantacaoEnabled || hubEnabled;
  const { papel: listaVidasPapel } = useListaVidasPapel();
  // AEX não tem carteira pós-venda própria — a aba Clientes Ativos some.
  const showMyPanel = !isAexWorkspace(workspace);
  const aexUser = userRole() === 'aex';
  const homeView = aexUser ? 'kanban' : 'dashboard';

  const primaryItems: NavItem[] = [
    { id: 'kanban',       label: 'Negócios',     view: 'kanban',       icon: FiBriefcase },
    { id: 'activities',   label: 'Atividades',   view: 'activities',   icon: FiCheckSquare },
    { id: 'agenda',       label: 'Agenda',       view: 'agenda',       icon: FiCalendar },
    { id: 'leads',        label: 'Leads',        view: 'leads',        icon: FiUsers },
    // Atendimento: só nos workspaces liberados no Super Admin (whatsapp_enabled).
    ...(whatsappEnabled ? [{ id: 'atendimento', label: 'Atendimento', view: 'atendimento', icon: FiMessageCircle } as NavItem] : []),
    // Empresas: só nos workspaces Enterprise (feature-flag empresas_enabled).
    ...(enterpriseWorkspace ? [{ id: 'empresas', label: 'Empresas', view: 'empresas', icon: FiGrid } as NavItem] : []),
    // Clientes (Hub + Implantação): não depende do workspace ativo, e sim
    // de a pessoa ser do grupo de Implantação OU a RPC do Hub liberar
    // (owner, CS). O item é conveniência — a rota e as RPCs barram.
    ...(clientesEnabled ? [{ id: 'clientes', label: 'Clientes', view: 'clientes', icon: FiClipboard } as NavItem] : []),
    // Lista de Vidas: só com papel em lista_vidas.papeis, em qualquer workspace.
    ...(listaVidasPapel ? [{ id: 'lista-vidas', label: 'Lista de Vidas', view: 'lista-vidas', icon: FiList } as NavItem] : []),
    { id: 'goals',        label: 'Metas',        view: 'goals',         icon: FiTarget },
    { id: 'commissions',  label: 'Comissões',    view: 'commissions',    icon: FiDollarSign },
  ].filter(item => !enterpriseWorkspace || !['goals', 'commissions'].includes(item.id));

  const handleTabClick = (view: string) => {
    onViewChange(view);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const loadWorkspaceOptions = async () => {
    setWorkspaceLoading(true);
    try {
      const rows = await listWorkspaces();
      setWorkspaceOptions(rows || []);
    } catch (error) {
      console.error('[Navigation] Erro ao listar workspaces:', error);
      setWorkspaceOptions([]);
    } finally {
      setWorkspaceLoading(false);
    }
  };

  const handleWorkspaceSelect = async (workspaceId: string) => {
    if (!workspaceId || workspaceId === workspace?.id || workspaceSwitching) return;
    setWorkspaceSwitching(workspaceId);
    try {
      await switchWorkspace(workspaceId);
      await queryClient.invalidateQueries();
    } catch (error) {
      console.error('[Navigation] Erro ao trocar workspace:', error);
    } finally {
      setWorkspaceSwitching(null);
    }
  };

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <button
        key={item.id}
        className={`h-[38px] flex items-center gap-2 px-3 border border-transparent rounded-lg bg-transparent text-white/[0.66] font-sans text-sm font-bold whitespace-nowrap cursor-pointer transition-colors hover:text-white hover:bg-white/[0.07] max-[1320px]:px-2.5 ${activeView === item.view ? 'text-white bg-[rgba(37,99,200,0.34)] border-[rgba(96,165,250,0.5)]' : ''}`}
        onClick={() => handleTabClick(item.view)}
        onMouseEnter={() => prefetchRoute(queryClient, item.view, workspace?.id)}
        type="button"
      >
        <Icon className="w-[15px] h-[15px] shrink-0 max-[1120px]:hidden" />
        <span>{item.label}</span>
      </button>
    );
  };

  return (
    <nav className="h-[58px] flex items-center justify-between gap-3.5 px-4 sticky top-0 z-[300] bg-navy border-b border-white/[0.08] text-white max-[760px]:h-auto max-[760px]:items-stretch max-[760px]:flex-col max-[760px]:px-3 max-[760px]:py-2.5">
      <div className="flex items-center flex-1 min-w-0 gap-2.5 max-[760px]:w-full">
        <button className="w-[132px] h-[42px] flex items-center justify-start p-0 bg-transparent border-0 cursor-pointer shrink-0" onClick={() => handleTabClick(homeView)} onMouseEnter={() => prefetchRoute(queryClient, homeView, workspace?.id)} aria-label={homeView === 'dashboard' ? 'Ir para Dashboard' : 'Ir para Negócios'}>
          <img src={logoHorizontal} alt="ED" className="w-[126px] h-[40px] object-contain block" />
        </button>

        <div className="w-px h-6 shrink-0 bg-white/[0.14]" />

        <div className="flex items-center gap-1 min-w-0 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden max-[760px]:flex-1">
          {primaryItems.map(renderNavItem)}
        </div>
      </div>

      <div className="flex items-center shrink-0 gap-2.5 max-[760px]:w-full max-[760px]:justify-end">
        {showMyPanel && (
          <button
            className={`h-9 flex items-center justify-center rounded-lg font-sans text-xs font-extrabold cursor-pointer gap-2 px-3.5 border border-[rgba(96,165,250,0.45)] bg-[rgba(37,99,200,0.22)] text-white whitespace-nowrap transition-colors hover:bg-[rgba(37,99,200,0.42)] hover:border-[rgba(147,197,253,0.72)] [&>svg]:w-[15px] [&>svg]:h-[15px] max-[1120px]:w-[38px] max-[1120px]:px-0 ${activeView === 'my-panel' ? 'bg-[rgba(37,99,200,0.42)] border-[rgba(147,197,253,0.72)]' : ''}`}
            onClick={() => handleTabClick('my-panel')}
            type="button"
          >
            <FiPieChart />
            <span className="max-[1120px]:hidden">{enterpriseWorkspace ? 'Clientes Ativos' : 'Meu Painel'}</span>
          </button>
        )}

        {isSuperAdmin() && (
          <button
            className="h-9 w-[38px] flex items-center justify-center rounded-lg font-sans text-xs font-extrabold cursor-pointer border border-[rgba(248,113,113,0.3)] bg-[rgba(239,68,68,0.12)] text-danger-b hover:text-white hover:bg-white/[0.13]"
            onClick={() => handleTabClick('super-admin')}
            title="Super Admin"
            type="button"
          >
            <FiShield />
          </button>
        )}

        <NotificationBell />

        <SyncStatus />

        <DropdownMenu onOpenChange={(open) => {
          if (open) loadWorkspaceOptions();
        }}>
          <DropdownMenuTrigger asChild>
            <button
              className="h-9 max-w-[210px] inline-flex items-center gap-2 px-2.5 border border-transparent rounded-lg bg-transparent text-white/[0.72] font-sans text-xs font-extrabold cursor-pointer hover:text-white hover:bg-white/[0.08] hover:border-white/[0.14] data-[state=open]:text-white data-[state=open]:bg-white/[0.08] data-[state=open]:border-white/[0.14] [&>svg]:w-[14px] [&>svg]:h-[14px] [&>svg]:shrink-0 max-[1120px]:w-[38px] max-[1120px]:px-0 max-[1120px]:[&>svg]:hidden"
              type="button"
            >
              <span className="min-w-0 max-w-[135px] overflow-hidden text-ellipsis whitespace-nowrap max-[1120px]:hidden">{profile?.nome || 'Usuário'}</span>
              <FiChevronDown />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[220px]">
            <DropdownMenuLabel className="px-2 py-1 text-[10px] font-extrabold uppercase tracking-[0.08em] text-text3">
              Workspace
            </DropdownMenuLabel>
            {workspaceLoading ? (
              <DropdownMenuItem disabled className="font-bold text-xs text-text3">
                Carregando...
              </DropdownMenuItem>
            ) : workspaceOptions.length > 0 ? (
              workspaceOptions.map((item) => {
                const ws = item.workspaces;
                const isActive = item.workspace_id === workspace?.id;
                const isSwitching = workspaceSwitching === item.workspace_id;
                return (
                  <DropdownMenuItem
                    key={item.workspace_id}
                    className="font-bold text-xs"
                    disabled={isActive || !!workspaceSwitching}
                    onSelect={() => handleWorkspaceSelect(item.workspace_id)}
                  >
                    <FiBriefcase />
                    <span className="min-w-0 flex-1 truncate">
                      {ws?.name || item.workspace_id}
                    </span>
                    {isSwitching ? (
                      <span className="text-[10px] text-text3">...</span>
                    ) : isActive ? (
                      <FiCheck className="text-success" />
                    ) : null}
                  </DropdownMenuItem>
                );
              })
            ) : (
              <DropdownMenuItem disabled className="font-bold text-xs text-text3">
                Nenhum workspace
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="font-extrabold text-xs"
              onSelect={() => handleTabClick('settings')}
            >
              <FiSettings />
              <span>Configurações</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="font-extrabold text-xs text-danger focus:text-danger"
              onSelect={handleLogout}
            >
              <FiLogOut />
              <span>Sair</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};

export default Navigation;
